import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  serverTimestamp,
  getDocs,
  increment,
  QueryDocumentSnapshot,
  Unsubscribe,
  DocumentSnapshot,
  arrayUnion,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import type { RootState } from "./store";

// ─────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────

export interface IMessage {
  id: string;
  senderId: string;
  text: string;
  timestamp: number | null;
  read: boolean;
  isAIReply?: boolean;
}

export interface IThread {
  threadId: string;
  participants: string[];
  otherUserId: string;
  lastMessage: {
    text: string;
    senderId: string;
    timestamp: number | null;
  } | null;
  unread: Record<string, number>;
  updatedAt: number | null;
  createdAt: number | null;
}

interface IMessageState {
  threads: IThread[];
  threadsLoading: boolean;
  messages: Record<string, IMessage[]>;
  messagesLoading: boolean;
  hasMore: Record<string, boolean>;
  paginating: boolean;
  error: string | null;
  sendingIds: string[];
}

const initialState: IMessageState = {
  threads:         [],
  threadsLoading:  false,
  messages:        {},
  messagesLoading: false,
  hasMore:         {},
  paginating:      false,
  error:           null,
  sendingIds:      [],
};

const PAGE_SIZE = 30;

const db   = getFirestore();
const auth = getAuth();

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

export const makeThreadId = (uid1: string, uid2: string): string =>
  [uid1, uid2].sort().join("_");

const threadRef = (threadId: string) =>
  doc(db, "messages", threadId);

const messagesColRef = (threadId: string) =>
  collection(db, "messages", threadId, "messages");

const inboxRootRef = (userId: string) =>
  doc(db, "inboxes", userId);

const inboxThreadRef = (userId: string, threadId: string) =>
  doc(db, "inboxes", userId, "threads", threadId);

const toMs = (ts: any): number | null => {
  if (!ts) return null;
  if (ts?.toMillis) return ts.toMillis();
  if (ts?.seconds)  return ts.seconds * 1000;
  if (typeof ts === "number") return ts;
  return null;
};

const serializeMessage = (snap: QueryDocumentSnapshot): IMessage => {
  const d = snap.data();
  return {
    id:        snap.id,
    senderId:  d.senderId  ?? "",
    text:      d.text      ?? "",
    timestamp: toMs(d.timestamp),
    read:      d.read      ?? false,
    isAIReply: d.isAIReply ?? false,
  };
};

// Prefer stored otherUserId field, then derive from participants
const serializeThread = (snap: DocumentSnapshot, currentUserId: string): IThread | null => {
  const d = snap.data();
  if (!d) return null;

  const participants: string[] = d.participants ?? [];

  const otherUserId: string =
    (typeof d.otherUserId === "string" && d.otherUserId !== "" ? d.otherUserId : null) ??
    participants.find(p => p !== currentUserId) ??
    "";

  return {
    threadId:    snap.id,
    participants,
    otherUserId,
    lastMessage: d.lastMessage
      ? { ...d.lastMessage, timestamp: toMs(d.lastMessage.timestamp) }
      : null,
    unread:    d.unread   ?? {},
    updatedAt: toMs(d.updatedAt),
    createdAt: toMs(d.createdAt),
  };
};

// ─────────────────────────────────────────────────────────────
// UNSUBSCRIBE REGISTRY
// ─────────────────────────────────────────────────────────────

const _unsubs = new Map<string, Unsubscribe>();

const registerUnsub = (key: string, fn: Unsubscribe) => {
  _unsubs.get(key)?.();
  _unsubs.set(key, fn);
};

export const unsubscribeIMessage = (key?: string) => {
  if (key) { _unsubs.get(key)?.(); _unsubs.delete(key); }
  else      { _unsubs.forEach(fn => fn()); _unsubs.clear(); }
};

const _cursors = new Map<string, QueryDocumentSnapshot>();

// ─────────────────────────────────────────────────────────────
// INBOX ROOT UPSERT
// ─────────────────────────────────────────────────────────────

const upsertInboxRoot = async (
  userId: string,
  otherUserId: string,
  threadId: string,
) => {
  await setDoc(
    inboxRootRef(userId),
    {
      userId,
      participantIds: arrayUnion(otherUserId),
      threadIds:      arrayUnion(threadId),
      updatedAt:      serverTimestamp(),
    },
    { merge: true },
  );
};

// ─────────────────────────────────────────────────────────────
// THUNKS
// ─────────────────────────────────────────────────────────────

export const createOrGetThread = createAsyncThunk<
  IThread,
  { otherUserId: string },
  { state: RootState }
>("imessage/createOrGetThread", async ({ otherUserId }, { getState, rejectWithValue }) => {
  try {
    const currentUserId = auth.currentUser?.uid || getState().auth.user?.userId;
    if (!currentUserId) throw new Error("Not authenticated");

    const threadId = makeThreadId(currentUserId, otherUserId);
    const now      = serverTimestamp();

    // setDoc merge — safe whether doc exists or not
    await setDoc(threadRef(threadId), {
      threadId,
      participants: [currentUserId, otherUserId],
      updatedAt:    now,
      createdAt:    now,
    }, { merge: true });

    await setDoc(inboxThreadRef(currentUserId, threadId), {
      threadId,
      participants: [currentUserId, otherUserId],
      otherUserId,
      updatedAt:    now,
      createdAt:    now,
    }, { merge: true });

    await setDoc(inboxThreadRef(otherUserId, threadId), {
      threadId,
      participants: [currentUserId, otherUserId],
      otherUserId:  currentUserId,
      updatedAt:    now,
      createdAt:    now,
    }, { merge: true });

    await Promise.all([
      upsertInboxRoot(currentUserId, otherUserId, threadId),
      upsertInboxRoot(otherUserId, currentUserId, threadId),
    ]);

    const fresh = await getDoc(threadRef(threadId));
    return serializeThread(fresh, currentUserId) as IThread;

  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

export const sendIMessage = createAsyncThunk<
  void,
  { threadId: string; otherUserId: string; text: string; optimisticId: string },
  { state: RootState }
>(
  "imessage/sendMessage",
  async ({ threadId, otherUserId, text, optimisticId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const currentUserId = auth.currentUser?.uid || getState().auth.user?.userId;
      if (!currentUserId) throw new Error("Not authenticated");
      if (!otherUserId)   throw new Error("otherUserId is required");

      const now = serverTimestamp();

      // 1. Ensure root thread doc exists with participants
      await setDoc(threadRef(threadId), {
        threadId,
        participants: [currentUserId, otherUserId],
        createdAt:    now,
      }, { merge: true });

      // 2. Write the message
      const msgRef = await addDoc(messagesColRef(threadId), {
        senderId:  currentUserId,
        text,
        timestamp: now,
        read:      false,
      });

      const lastMessageData = { text, senderId: currentUserId, timestamp: now };

      // 3. Update root thread doc lastMessage + unread
      await setDoc(threadRef(threadId), {
        lastMessage:                 lastMessageData,
        updatedAt:                   now,
        [`unread.${currentUserId}`]: 0,
        [`unread.${otherUserId}`]:   increment(1),
      }, { merge: true });

      // 4. Upsert BOTH inbox thread docs — setDoc merge creates if missing
      await Promise.all([
        setDoc(inboxThreadRef(currentUserId, threadId), {
          threadId,
          participants:                [currentUserId, otherUserId],
          otherUserId,
          lastMessage:                 lastMessageData,
          updatedAt:                   now,
          createdAt:                   now,
          [`unread.${currentUserId}`]: 0,
          [`unread.${otherUserId}`]:   increment(1),
        }, { merge: true }),
        setDoc(inboxThreadRef(otherUserId, threadId), {
          threadId,
          participants:                [currentUserId, otherUserId],
          otherUserId:                 currentUserId,
          lastMessage:                 lastMessageData,
          updatedAt:                   now,
          createdAt:                   now,
          [`unread.${currentUserId}`]: 0,
          [`unread.${otherUserId}`]:   increment(1),
        }, { merge: true }),
      ]);

      // 5. Upsert both inbox root docs
      await Promise.all([
        upsertInboxRoot(currentUserId, otherUserId, threadId),
        upsertInboxRoot(otherUserId, currentUserId, threadId),
      ]);

      dispatch(confirmOptimisticMessage({ optimisticId, confirmedId: msgRef.id }));

    } catch (err: any) {
      dispatch(failOptimisticMessage({ optimisticId }));
      return rejectWithValue(err.message);
    }
  }
);

export const listenToInbox = createAsyncThunk<
  void,
  { currentUserId: string },
  { state: RootState }
>("imessage/listenToInbox", async ({ currentUserId }, { dispatch }) => {
  if (!currentUserId) return;

  const q = query(
    collection(db, "inboxes", currentUserId, "threads"),
    orderBy("updatedAt", "desc"),
  );

  const unsub = onSnapshot(q, snap => {
    const threads: IThread[] = snap.docs
      .map(d => serializeThread(d, currentUserId))
      .filter(Boolean) as IThread[];
    dispatch(setThreads(threads));
  }, err => {
    console.error("[listenToInbox] error:", err.message);
  });

  registerUnsub(`inbox_${currentUserId}`, unsub);
});

export const listenToIMessages = createAsyncThunk<
  void,
  { threadId: string },
  { state: RootState }
>("imessage/listenToMessages", async ({ threadId }, { dispatch }) => {
  const q = query(
    messagesColRef(threadId),
    orderBy("timestamp", "desc"),
    limit(PAGE_SIZE),
  );

  const unsub = onSnapshot(q, snap => {
    if (!snap.empty) _cursors.set(threadId, snap.docs[snap.docs.length - 1]);
    const msgs = snap.docs.map(serializeMessage).reverse();
    dispatch(setMessages({ threadId, messages: msgs }));
  }, err => {
    console.error("[listenToIMessages] error:", err.message);
  });

  registerUnsub(`messages_${threadId}`, unsub);
});

export const loadOlderIMessages = createAsyncThunk<
  { threadId: string; messages: IMessage[] },
  { threadId: string },
  { state: RootState }
>("imessage/loadOlderMessages", async ({ threadId }, { rejectWithValue }) => {
  try {
    const cursor = _cursors.get(threadId);
    if (!cursor) return { threadId, messages: [] };

    const q = query(
      messagesColRef(threadId),
      orderBy("timestamp", "desc"),
      startAfter(cursor),
      limit(PAGE_SIZE),
    );

    const snap = await getDocs(q);
    if (!snap.empty) _cursors.set(threadId, snap.docs[snap.docs.length - 1]);

    return { threadId, messages: snap.docs.map(serializeMessage).reverse() };
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

export const markIThreadAsRead = createAsyncThunk<
  void,
  { threadId: string },
  { state: RootState }
>("imessage/markAsRead", async ({ threadId }, { getState, rejectWithValue }) => {
  try {
    const currentUserId = auth.currentUser?.uid || getState().auth.user?.userId;
    if (!currentUserId) return;

    const update = { [`unread.${currentUserId}`]: 0 };

    await Promise.all([
      setDoc(threadRef(threadId),                     update, { merge: true }),
      setDoc(inboxThreadRef(currentUserId, threadId), update, { merge: true }),
    ]);
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

export const deleteIThread = createAsyncThunk<
  string,
  { threadId: string },
  { state: RootState }
>("imessage/deleteThread", async ({ threadId }, { getState, rejectWithValue }) => {
  try {
    const currentUserId = auth.currentUser?.uid || getState().auth.user?.userId;
    if (!currentUserId) throw new Error("Not authenticated");

    await setDoc(inboxThreadRef(currentUserId, threadId), {
      deletedBy: currentUserId,
      deletedAt: serverTimestamp(),
    }, { merge: true });

    unsubscribeIMessage(`messages_${threadId}`);
    return threadId;
  } catch (err: any) {
    return rejectWithValue(err.message);
  }
});

// ─────────────────────────────────────────────────────────────
// SLICE
// ─────────────────────────────────────────────────────────────

const iMessageSlice = createSlice({
  name: "imessage",
  initialState,

  reducers: {
    setThreads(state, action: PayloadAction<IThread[]>) {
      state.threads = action.payload;
    },

    setMessages(
      state,
      action: PayloadAction<{ threadId: string; messages: IMessage[] }>
    ) {
      const { threadId, messages } = action.payload;
      const existing              = state.messages[threadId] ?? [];
      const incomingIds           = new Set(messages.map(m => m.id));
      const pendingOptimistic     = existing.filter(
        m => (m as any).optimistic && !incomingIds.has(m.id)
      );

      const merged = [...messages, ...pendingOptimistic];
      const seen   = new Map<string, IMessage>();

      for (const msg of merged) {
        const ex = seen.get(msg.id);
        if (!ex || (!(msg as any).optimistic && (ex as any).optimistic)) {
          seen.set(msg.id, msg);
        }
      }

      state.messages[threadId] = Array.from(seen.values()).sort(
        (a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0)
      );
    },

    addOptimisticIMessage(
      state,
      action: PayloadAction<{ threadId: string; message: IMessage & { optimistic: true } }>
    ) {
      const { threadId, message } = action.payload;
      if (!state.messages[threadId]) state.messages[threadId] = [];
      state.messages[threadId].push(message);
      state.sendingIds.push(message.id);
    },

    confirmOptimisticMessage(
      state,
      action: PayloadAction<{ optimisticId: string; confirmedId: string }>
    ) {
      const { optimisticId, confirmedId } = action.payload;
      for (const threadId of Object.keys(state.messages)) {
        const msg = state.messages[threadId].find(m => m.id === optimisticId);
        if (msg) {
          msg.id = confirmedId;
          (msg as any).optimistic = false;
          break;
        }
      }
      state.sendingIds = state.sendingIds.filter(id => id !== optimisticId);
    },

    failOptimisticMessage(
      state,
      action: PayloadAction<{ optimisticId: string }>
    ) {
      const { optimisticId } = action.payload;
      for (const threadId of Object.keys(state.messages)) {
        const msg = state.messages[threadId].find(m => m.id === optimisticId);
        if (msg) { (msg as any).failed = true; break; }
      }
      state.sendingIds = state.sendingIds.filter(id => id !== optimisticId);
    },

    clearThreadMessages(state, action: PayloadAction<string>) {
      delete state.messages[action.payload];
      delete state.hasMore[action.payload];
      _cursors.delete(action.payload);
    },

    clearIMessageState() {
      unsubscribeIMessage();
      _cursors.clear();
      return initialState;
    },
  },

  extraReducers: builder => {
    builder
      .addCase(createOrGetThread.pending,   s => { s.threadsLoading = true;  s.error = null; })
      .addCase(createOrGetThread.fulfilled, s => { s.threadsLoading = false; })
      .addCase(createOrGetThread.rejected,  (s, a) => { s.threadsLoading = false; s.error = a.payload as string; });

    builder
      .addCase(listenToInbox.pending,   s => { s.threadsLoading = true; })
      .addCase(listenToInbox.fulfilled, s => { s.threadsLoading = false; })
      .addCase(listenToInbox.rejected,  s => { s.threadsLoading = false; });

    builder
      .addCase(listenToIMessages.pending,   s => { s.messagesLoading = true; })
      .addCase(listenToIMessages.fulfilled, s => { s.messagesLoading = false; })
      .addCase(listenToIMessages.rejected,  s => { s.messagesLoading = false; });

    builder
      .addCase(loadOlderIMessages.pending,   s => { s.paginating = true; })
      .addCase(loadOlderIMessages.fulfilled, (s, a) => {
        s.paginating = false;
        const { threadId, messages } = a.payload;
        if (messages.length < PAGE_SIZE) s.hasMore[threadId] = false;
        const existingIds = new Set((s.messages[threadId] ?? []).map(m => m.id));
        const fresh       = messages.filter(m => !existingIds.has(m.id));
        s.messages[threadId] = [...fresh, ...(s.messages[threadId] ?? [])];
      })
      .addCase(loadOlderIMessages.rejected, s => { s.paginating = false; });

    builder.addCase(deleteIThread.fulfilled, (s, a) => {
      s.threads = s.threads.filter(t => t.threadId !== a.payload);
      delete s.messages[a.payload];
      delete s.hasMore[a.payload];
    });
  },
});

export const {
  setThreads,
  setMessages,
  addOptimisticIMessage,
  confirmOptimisticMessage,
  failOptimisticMessage,
  clearThreadMessages,
  clearIMessageState,
} = iMessageSlice.actions;

export default iMessageSlice.reducer;